// Route bootstrap for /auth/reset-password. Runs at module import — before
// createClient — so the credential-bearing URL is captured into private
// memory and scrubbed from history before the SDK can auto-import it.
// Route classification is independent of capture/scrub success: every load
// of the exact reset route gets the ephemeral client even when history is
// unavailable, so the SDK can never auto-import a sensitive URL.
// The captured snapshot lives in module memory until the page unloads or the
// app leaves the route; it is never written to storage, logs, or the DOM.

import { WEB_RECOVERY_PATH } from './nativeRecoveryContinuation';

export interface CapturedRecoveryRoute {
  pathname: string;
  search: string;
  hash: string;
}

let captured: CapturedRecoveryRoute | null = null;
let recoveryRouteLoad = false;

export const RECOVERY_AUTH_OPTIONS = {
  detectSessionInUrl: false,
  persistSession: false,
  autoRefreshToken: false,
} as const;

const captureInitialRecoveryRoute = (): void => {
  if (typeof window === 'undefined' || !window.location) return;
  const { pathname, search, hash } = window.location;
  if (pathname !== WEB_RECOVERY_PATH) return;
  recoveryRouteLoad = true;
  if (!search && !hash) return;
  captured = { pathname, search, hash };
  try {
    window.history.replaceState(null, '', pathname);
  } catch {
    // History scrub failed — the snapshot stays captured and the ephemeral
    // client still blocks auto-import; credentials simply remain in history.
  }
};

captureInitialRecoveryRoute();

export const getCapturedRecoveryRoute = (): CapturedRecoveryRoute | null => captured;

export const wasRecoveryRouteLoad = (): boolean => recoveryRouteLoad;

// Called when the app actually leaves the reset route (not on StrictMode's
// fake unmount — the window pathname still points at the reset path there).
export const clearCapturedRecoveryRoute = (): void => {
  captured = null;
};

// Re-runs the bootstrap capture against the current window location — the
// seam tests use to simulate a fresh route load without module isolation.
export const recaptureRecoveryRoute = (): void => {
  captured = null;
  recoveryRouteLoad = false;
  captureInitialRecoveryRoute();
};

// The page uses the scrubbed snapshot while the live router location still
// points at the bare route; SPA navigation and tests use the live location.
export const isCapturedRouteCurrent = (live: {
  pathname: string;
  search: string;
  hash: string;
}): boolean =>
  !!captured &&
  live.pathname === captured.pathname &&
  live.search === '' &&
  live.hash === '';
